import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import {
  TrendingUp,
  Phone,
  DollarSign,
  Users,
  Target,
  Award,
  Bell,
  MessageSquare,
  Mail,
  Brain,
  Clock,
  ChevronRight,
  LogOut,
  BarChart3,
  Calendar,
  BookOpen,
  Search,
  Filter,
  RefreshCw,
  Trophy,
  Zap,
  Send,
  Copy,
  Check,
  ExternalLink,
  History,
  FileText,
  GraduationCap,
  PlayCircle,
  CheckCircle,
  Eye,
  MailOpen,
  MailX,
  Megaphone,
  Database,
  PhoneOff,
  Volume2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

import CRMDataTracker from '../components/CRMDataTracker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth axios instance
const authAxios = axios.create();
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Sidebar Navigation
const Sidebar = ({ activeTab, setActiveTab, agentName, onLogout, uncalledCount }) => {
  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3, color: "text-purple-400" },
    { id: "my-data", label: "Datalarım", icon: Database, color: "text-blue-500", badge: uncalledCount },
    { id: "whatsapp", label: "WhatsApp", icon: MessageSquare, color: "text-green-400" },
    { id: "email", label: "E-posta", icon: Mail, color: "text-blue-400" },
    { id: "customers", label: "Müşterilerim", icon: Users, color: "text-emerald-400" },
    { id: "templates", label: "Satış Şablonları", icon: BookOpen, color: "text-pink-400" },
    { id: "training", label: "Oryantasyon Eğitimler", icon: GraduationCap, color: "text-orange-400" },
  ];

  return (
    <div className="w-64 bg-slate-50/80 border-r border-slate-200 flex flex-col h-screen">
      {/* Header */}
      <div className="p-6 border-b border-slate-200">
        <h1 className="font-heading text-xl font-bold text-slate-800">Danışman Paneli</h1>
        <p className="text-sm text-slate-500 mt-1">{agentName}</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
              activeTab === item.id
                ? "bg-purple-500/20 text-slate-800"
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            }`}
          >
            <item.icon size={20} className={item.color} />
            <span className="font-medium flex-1 text-left">{item.label}</span>
            {item.badge > 0 && (
              <span className="bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-testid="sidebar-data-badge">
                {item.badge}
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-slate-200">
        <Button
          variant="ghost"
          className="w-full justify-start text-slate-500 hover:text-red-400"
          onClick={onLogout}
        >
          <LogOut size={18} className="mr-2" />
          Çıkış Yap
        </Button>
      </div>
    </div>
  );
};

// Dashboard Tab
const DashboardTab = ({ dashboardData, chartData, newDataInfo }) => {
  if (!dashboardData) return <div className="flex items-center justify-center h-64"><RefreshCw className="animate-spin" /></div>;

  const stats = [
    { label: "Bugün Satış", value: dashboardData.today?.sales_count || 0, icon: DollarSign, color: "from-emerald-500 to-emerald-600", subtext: `₺${(dashboardData.today?.revenue || 0).toLocaleString('tr-TR')}` },
    { label: "Bugün Arama", value: dashboardData.today?.calls || 0, icon: Phone, color: "from-blue-500 to-blue-600" },
    { label: "Bu Ay Satış", value: dashboardData.month?.sales_count || 0, icon: TrendingUp, color: "from-purple-500 to-purple-600", subtext: `₺${(dashboardData.month?.revenue || 0).toLocaleString('tr-TR')}` },
    { label: "Takım Sırası", value: `#${dashboardData.rank || '-'}`, icon: Trophy, color: "from-amber-500 to-amber-600", subtext: `${dashboardData.total_agents || 0} kişi` },
  ];

  const uncalledCount = newDataInfo?.uncalled_count || dashboardData.uncalled_data_count || 0;
  const newTodayCount = newDataInfo?.new_today || 0;

  return (
    <div className="space-y-6">
      {/* New Data Alert Banner */}
      {(uncalledCount > 0 || newTodayCount > 0) && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between"
          data-testid="agent-new-data-alert"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <PhoneOff size={20} className="text-orange-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-800">Aranmayan Data Bilgisi</p>
              <p className="text-sm text-slate-600">
                <span className="font-bold text-orange-600">{uncalledCount}</span> adet aranmayan data mevcut
                {newTodayCount > 0 && <span className="ml-2 text-emerald-600 font-medium">({newTodayCount} yeni bugün)</span>}
              </p>
            </div>
          </div>
          {uncalledCount > 0 && (
            <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full animate-pulse" data-testid="uncalled-data-badge">
              {uncalledCount}
            </span>
          )}
        </motion.div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {stats.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
          >
            <Card className="bg-white border-slate-200 overflow-hidden">
              <div className={`h-1 bg-gradient-to-r ${stat.color}`} />
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm">{stat.label}</p>
                    <p className="text-3xl font-bold text-slate-800 mt-1">{stat.value}</p>
                    {stat.subtext && <p className="text-xs text-slate-500 mt-1">{stat.subtext}</p>}
                  </div>
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color} bg-opacity-20`}>
                    <stat.icon size={24} className="text-slate-800" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
        {/* Aranmayan Data Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className={`border overflow-hidden ${uncalledCount > 0 ? 'bg-orange-50 border-orange-200' : 'bg-white border-slate-200'}`} data-testid="uncalled-data-card">
            <div className="h-1 bg-gradient-to-r from-orange-500 to-red-500" />
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-500 text-sm">Aranmayan Data</p>
                  <p className={`text-3xl font-bold mt-1 ${uncalledCount > 0 ? 'text-orange-600' : 'text-slate-800'}`}>{uncalledCount}</p>
                  <p className="text-xs text-slate-500 mt-1">bekleyen arama</p>
                </div>
                <div className={`p-3 rounded-xl ${uncalledCount > 0 ? 'bg-orange-100' : 'bg-slate-100'}`}>
                  <PhoneOff size={24} className={uncalledCount > 0 ? 'text-orange-600' : 'text-slate-400'} />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Performance Chart */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 size={20} className="text-purple-400" />
            Haftalık Performans
          </CardTitle>
        </CardHeader>
        <CardContent>
          {chartData && chartData.length > 0 ? (
            <div className="h-64 flex items-end gap-2">
              {chartData.slice(-7).map((day, i) => {
                const maxSales = Math.max(...chartData.slice(-7).map(d => d.sales || 1), 1);
                const height = ((day.sales || 0) / maxSales) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-2">
                    <div 
                      className="w-full bg-gradient-to-t from-purple-600 to-purple-400 rounded-t-lg transition-all"
                      style={{ height: `${Math.max(height, 5)}%` }}
                    />
                    <span className="text-xs text-slate-500">{day.date?.slice(5)}</span>
                    <span className="text-xs font-medium text-slate-800">{day.sales}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-slate-500">
              Veri yükleniyor...
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white border-slate-200 hover:border-purple-500/50 cursor-pointer transition-all">
          <CardContent className="p-4 flex flex-col items-center text-center">
            <MessageSquare size={24} className="text-green-400 mb-2" />
            <p className="text-sm text-slate-800">WhatsApp Şablonları</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200 hover:border-purple-500/50 cursor-pointer transition-all">
          <CardContent className="p-4 flex flex-col items-center text-center">
            <Mail size={24} className="text-blue-400 mb-2" />
            <p className="text-sm text-slate-800">E-posta Şablonları</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200 hover:border-purple-500/50 cursor-pointer transition-all">
          <CardContent className="p-4 flex flex-col items-center text-center">
            <Brain size={24} className="text-purple-400 mb-2" />
            <p className="text-sm text-slate-800">AI Önerisi Al</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200 hover:border-purple-500/50 cursor-pointer transition-all">
          <CardContent className="p-4 flex flex-col items-center text-center">
            <Bell size={24} className="text-orange-400 mb-2" />
            <p className="text-sm text-slate-800">Hatırlatıcı Ekle</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Customers Tab
const CustomersTab = ({ customers, onSelectCustomer }) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const statusOptions = [
    { value: "all", label: "Tüm Durumlar" },
    { value: "DÜŞÜNECEK", label: "Düşünecek" },
    { value: "KAYIT AŞAMASI-AYNI GÜN", label: "Kayıt Aşaması" },
    { value: "CEVAP YOK", label: "Cevap Yok" },
    { value: "İSTEMİYOR", label: "İstemiyor" },
  ];

  const filtered = customers.filter(c => {
    if (statusFilter !== "all" && c.latest_status !== statusFilter) return false;
    if (search && !c.customer_name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            placeholder="Müşteri ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white border-slate-300"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 bg-white border-slate-300">
            <Filter size={14} className="mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white text-slate-800">
            {statusOptions.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-white border-slate-200">
        <ScrollArea className="h-[600px]">
          <div className="divide-y divide-slate-200">
            {filtered.map((customer, i) => (
              <motion.div
                key={customer.customer_name + i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-4 hover:bg-slate-50 cursor-pointer transition-all"
                onClick={() => onSelectCustomer && onSelectCustomer(customer)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-800">{customer.customer_name}</p>
                    <p className="text-sm text-slate-500">{customer.ders_adi || '-'}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-1 rounded ${
                      customer.latest_status?.includes("KAYIT") ? 'bg-emerald-500/20 text-emerald-400' :
                      customer.latest_status?.includes("DÜŞÜNECEK") ? 'bg-blue-500/20 text-blue-400' :
                      customer.latest_status?.includes("İSTEMİYOR") ? 'bg-red-500/20 text-red-400' :
                      'bg-slate-300 text-slate-500'
                    }`}>
                      {customer.latest_status}
                    </span>
                    <p className="text-xs text-slate-400 mt-1">{customer.call_count} arama</p>
                  </div>
                </div>
                {customer.latest_note && (
                  <p className="text-xs text-slate-500 mt-2 truncate">{customer.latest_note}</p>
                )}
              </motion.div>
            ))}
          </div>
        </ScrollArea>
      </Card>
    </div>
  );
};

// Templates Tab
const TemplatesTab = ({ templates }) => {
  const [category, setCategory] = useState("all");
  const [copied, setCopied] = useState(null);

  const categories = [
    { value: "all", label: "Tümü" },
    { value: "opening", label: "Açılış Cümleleri" },
    { value: "objection", label: "İtiraz Karşılama" },
    { value: "closing", label: "Kapanış Teknikleri" },
    { value: "whatsapp", label: "WhatsApp" },
    { value: "email", label: "E-posta" },
    { value: "hypnotic", label: "Hipnotik Satış" },
  ];

  const filtered = category === "all" 
    ? templates 
    : templates.filter(t => t.category === category);

  const copyTemplate = (content, id) => {
    navigator.clipboard.writeText(content);
    setCopied(id);
    toast.success("Şablon kopyalandı!");
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {categories.map(cat => (
          <Button
            key={cat.value}
            variant={category === cat.value ? "default" : "outline"}
            size="sm"
            onClick={() => setCategory(cat.value)}
            className={category === cat.value ? "bg-purple-600" : "border-slate-300"}
          >
            {cat.label}
          </Button>
        ))}
      </div>

      <div className="grid gap-4">
        {filtered.map((template) => (
          <Card key={template.id} className="bg-white border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{template.title}</CardTitle>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyTemplate(template.content, template.id)}
                >
                  {copied === template.id ? "Kopyalandı!" : "Kopyala"}
                </Button>
              </div>
              <div className="flex gap-1">
                {template.tags?.map(tag => (
                  <span key={tag} className="text-xs px-2 py-0.5 bg-slate-200 text-slate-500 rounded">
                    {tag}
                  </span>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{template.content}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// WhatsApp Tab - Send messages to customers
const WhatsAppTab = ({ customersWithPhone, whatsappTemplates, messageHistory, onSendMessage, onRefresh }) => {
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [search, setSearch] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState("send");
  const [copied, setCopied] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");

  // Filter customers by search
  const filteredCustomers = customersWithPhone.filter(c => 
    c.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.telefon?.includes(search)
  );

  // Select customer
  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerName(customer.customer_name || "");
    setPhone(customer.telefon || "");
    // Pre-fill message if template selected
    if (selectedTemplate) {
      applyTemplate(selectedTemplate, customer);
    }
  };

  // Apply template
  const applyTemplate = (template, customer = selectedCustomer) => {
    setSelectedTemplate(template);
    let content = template.content;
    // Replace variables
    if (customer) {
      content = content.replace(/{customer_name}/g, customer.customer_name || customerName || "");
      content = content.replace(/{course_name}/g, customer.ders_adi || "");
    }
    content = content.replace(/{days}/g, "3");
    content = content.replace(/{discount}/g, "20");
    setMessage(content);
  };

  // Generate WhatsApp link
  const generateLink = async () => {
    if (!phone || !message) {
      toast.error("Telefon ve mesaj gerekli");
      return;
    }

    try {
      const res = await authAxios.post(`${API}/whatsapp/generate-link`, { phone, message });
      setGeneratedLink(res.data.link);
      
      // Log the message
      await authAxios.post(`${API}/whatsapp/send-message`, {
        customer_name: customerName,
        phone: phone,
        message: message,
        template_name: selectedTemplate?.name || ""
      });
      
      toast.success("Link oluşturuldu!");
    } catch (error) {
      toast.error("Link oluşturulamadı");
    }
  };

  // Copy link
  const copyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    toast.success("Link kopyalandı!");
    setTimeout(() => setCopied(false), 2000);
  };

  // Open WhatsApp
  const openWhatsApp = () => {
    if (generatedLink) {
      window.open(generatedLink, "_blank");
    }
  };

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-4">
        <Button
          variant={activeSubTab === "send" ? "default" : "ghost"}
          onClick={() => setActiveSubTab("send")}
          className={activeSubTab === "send" ? "bg-green-600" : ""}
        >
          <Send size={16} className="mr-2" />
          Mesaj Gönder
        </Button>
        <Button
          variant={activeSubTab === "templates" ? "default" : "ghost"}
          onClick={() => setActiveSubTab("templates")}
          className={activeSubTab === "templates" ? "bg-green-600" : ""}
        >
          <FileText size={16} className="mr-2" />
          Şablonlar
        </Button>
        <Button
          variant={activeSubTab === "history" ? "default" : "ghost"}
          onClick={() => setActiveSubTab("history")}
          className={activeSubTab === "history" ? "bg-green-600" : ""}
        >
          <History size={16} className="mr-2" />
          Geçmiş
        </Button>
      </div>

      {/* Send Message Tab */}
      {activeSubTab === "send" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Customer List */}
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users size={18} className="text-emerald-400" />
                Müşteriler
              </CardTitle>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <Input
                  placeholder="Müşteri veya telefon ara..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 bg-white border-slate-300 text-sm"
                />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[400px]">
                <div className="divide-y divide-slate-200">
                  {filteredCustomers.map((customer, i) => (
                    <div
                      key={customer.customer_name + i}
                      onClick={() => handleSelectCustomer(customer)}
                      className={`p-3 cursor-pointer transition-all ${
                        selectedCustomer?.customer_name === customer.customer_name
                          ? "bg-green-500/10 border-l-2 border-green-500"
                          : "hover:bg-slate-100"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-800 text-sm">{customer.customer_name}</p>
                          <p className="text-xs text-slate-500">{customer.telefon}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          customer.latest_status?.includes("KAYIT") ? 'bg-emerald-500/20 text-emerald-400' :
                          customer.latest_status?.includes("DÜŞÜNECEK") ? 'bg-blue-500/20 text-blue-400' :
                          'bg-slate-300 text-slate-500'
                        }`}>
                          {customer.latest_status?.slice(0, 15)}
                        </span>
                      </div>
                    </div>
                  ))}
                  {filteredCustomers.length === 0 && (
                    <div className="p-8 text-center text-slate-500">
                      <Users size={32} className="mx-auto mb-2 opacity-50" />
                      <p>Telefon numarası olan müşteri bulunamadı</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Message Composer */}
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <MessageSquare size={18} className="text-green-400" />
                Mesaj Oluştur
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Manual phone input */}
              <div className="space-y-2">
                <label className="text-xs text-slate-500">Telefon Numarası</label>
                <Input
                  placeholder="0532 xxx xx xx"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="bg-white border-slate-300"
                />
              </div>

              {/* Customer name */}
              <div className="space-y-2">
                <label className="text-xs text-slate-500">Müşteri Adı</label>
                <Input
                  placeholder="Müşteri adı"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="bg-white border-slate-300"
                />
              </div>

              {/* Template selector */}
              <div className="space-y-2">
                <label className="text-xs text-slate-500">Şablon Seç</label>
                <Select onValueChange={(val) => {
                  const template = whatsappTemplates.find(t => t.id === val);
                  if (template) applyTemplate(template);
                }}>
                  <SelectTrigger className="bg-white border-slate-300">
                    <SelectValue placeholder="Şablon seçin..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-slate-800">
                    {whatsappTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Message textarea */}
              <div className="space-y-2">
                <label className="text-xs text-slate-500">Mesaj</label>
                <Textarea
                  placeholder="Mesajınızı yazın..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="bg-white border-slate-300 min-h-[120px]"
                />
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <Button 
                  onClick={generateLink}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={!phone || !message}
                >
                  <Send size={16} className="mr-2" />
                  Link Oluştur
                </Button>
              </div>

              {/* Generated link */}
              {generatedLink && (
                <div className="space-y-2 p-3 bg-slate-100 rounded-lg">
                  <p className="text-xs text-slate-500">WhatsApp Link:</p>
                  <div className="flex gap-2">
                    <Input
                      value={generatedLink}
                      readOnly
                      className="bg-slate-50 border-slate-300 text-xs"
                    />
                    <Button size="sm" variant="outline" onClick={copyLink}>
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                    </Button>
                    <Button size="sm" className="bg-green-600" onClick={openWhatsApp}>
                      <ExternalLink size={14} />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    "WhatsApp Aç" butonuna tıklayarak mesajı gönderin
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Templates Tab */}
      {activeSubTab === "templates" && (
        <div className="grid gap-4 md:grid-cols-2">
          {whatsappTemplates.map(template => (
            <Card key={template.id} className="bg-white border-slate-200">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{template.name}</CardTitle>
                  <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded">
                    {template.category}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-700 whitespace-pre-wrap mb-3">{template.content}</p>
                <div className="flex gap-2 flex-wrap">
                  {template.variables?.map(v => (
                    <span key={v} className="text-xs px-2 py-0.5 bg-slate-200 text-slate-500 rounded">
                      {`{${v}}`}
                    </span>
                  ))}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3 w-full"
                  onClick={() => {
                    applyTemplate(template);
                    setActiveSubTab("send");
                  }}
                >
                  Bu Şablonu Kullan
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* History Tab */}
      {activeSubTab === "history" && (
        <Card className="bg-white border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <History size={18} className="text-slate-500" />
              Mesaj Geçmişi
            </CardTitle>
            <Button size="sm" variant="ghost" onClick={onRefresh}>
              <RefreshCw size={14} />
            </Button>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <div className="space-y-3">
                {messageHistory.map((msg, i) => (
                  <div key={msg.id || i} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-slate-800">{msg.customer_name}</span>
                      <span className="text-xs text-slate-500">
                        {new Date(msg.sent_at).toLocaleString('tr-TR')}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500">{msg.customer_phone}</p>
                    <p className="text-sm text-slate-700 mt-2">{msg.content}</p>
                    {msg.template_used && (
                      <span className="text-xs text-green-400 mt-2 block">
                        Şablon: {msg.template_used}
                      </span>
                    )}
                  </div>
                ))}
                {messageHistory.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <History size={32} className="mx-auto mb-2 opacity-50" />
                    <p>Henüz mesaj geçmişi yok</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Email Tab - Send emails to customers
const EmailTab = ({ emailTemplates, emailHistory, emailStats, customers, onRefresh }) => {
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [subject, setSubject] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState("send");
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sending, setSending] = useState(false);

  const statusOptions = [
    { value: "all", label: "Tüm Durumlar" },
    { value: "CEVAP YOK", label: "Cevap Yok" },
    { value: "DÜŞÜNECEK", label: "Düşünecek" },
    { value: "İSTEMİYOR", label: "İstemiyor" },
    { value: "MEŞGUL", label: "Meşgul" },
  ];

  const filteredCustomers = customers.filter(c => {
    if (filterStatus !== "all" && c.latest_status !== filterStatus) return false;
    if (search && !c.customer_name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setRecipientName(customer.customer_name || "");
    // Try to get email from customer data
    const email = customer.email || "";
    if (email.includes("@")) {
      setRecipientEmail(email);
    }
    if (selectedTemplate) {
      applyTemplate(selectedTemplate, customer);
    }
  };

  const applyTemplate = (template, customer = selectedCustomer) => {
    setSelectedTemplate(template);
    let subj = template.subject;
    let content = template.html_content;
    
    if (customer) {
      subj = subj.replace(/{customer_name}/g, customer.customer_name || recipientName || "");
      subj = subj.replace(/{course_name}/g, customer.ders_adi || "");
      content = content.replace(/{customer_name}/g, customer.customer_name || recipientName || "");
      content = content.replace(/{course_name}/g, customer.ders_adi || "");
    }
    content = content.replace(/{discount}/g, "20");
    content = content.replace(/{days}/g, "7");
    
    setSubject(subj);
    setHtmlContent(content);
  };

  const sendEmail = async () => {
    if (!recipientEmail || !subject || !htmlContent) {
      toast.error("Email, konu ve içerik gerekli");
      return;
    }
    
    setSending(true);
    try {
      await authAxios.post(`${API}/email/send`, {
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        subject: subject,
        html_content: htmlContent,
        template_id: selectedTemplate?.id || ""
      });
      toast.success("Email gönderildi!");
      setRecipientEmail("");
      setRecipientName("");
      setSubject("");
      setHtmlContent("");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Email gönderilemedi");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Send size={20} className="text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{emailStats?.total_sent || 0}</p>
              <p className="text-xs text-slate-500">Gönderilen</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/20">
              <MailOpen size={20} className="text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{emailStats?.total_opened || 0}</p>
              <p className="text-xs text-slate-500">Açılan</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Eye size={20} className="text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">%{emailStats?.open_rate || 0}</p>
              <p className="text-xs text-slate-500">Açılma Oranı</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-4">
        <Button
          variant={activeSubTab === "send" ? "default" : "ghost"}
          onClick={() => setActiveSubTab("send")}
          className={activeSubTab === "send" ? "bg-blue-600" : ""}
        >
          <Send size={16} className="mr-2" />
          Mail Gönder
        </Button>
        <Button
          variant={activeSubTab === "templates" ? "default" : "ghost"}
          onClick={() => setActiveSubTab("templates")}
          className={activeSubTab === "templates" ? "bg-blue-600" : ""}
        >
          <FileText size={16} className="mr-2" />
          Şablonlar
        </Button>
        <Button
          variant={activeSubTab === "history" ? "default" : "ghost"}
          onClick={() => setActiveSubTab("history")}
          className={activeSubTab === "history" ? "bg-blue-600" : ""}
        >
          <History size={16} className="mr-2" />
          Geçmiş
        </Button>
        <Button
          variant={activeSubTab === "bulk" ? "default" : "ghost"}
          onClick={() => setActiveSubTab("bulk")}
          className={activeSubTab === "bulk" ? "bg-blue-600" : ""}
        >
          <Megaphone size={16} className="mr-2" />
          Toplu Gönderim
        </Button>
      </div>

      {/* Send Email Tab */}
      {activeSubTab === "send" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Customer List */}
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users size={18} className="text-emerald-400" />
                Müşteriler
              </CardTitle>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    placeholder="Müşteri ara..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 bg-white border-slate-300 text-sm"
                  />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-32 bg-white border-slate-300 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-slate-800">
                    {statusOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[400px]">
                <div className="divide-y divide-slate-200">
                  {filteredCustomers.map((customer, i) => (
                    <div
                      key={customer.customer_name + i}
                      onClick={() => handleSelectCustomer(customer)}
                      className={`p-3 cursor-pointer transition-all ${
                        selectedCustomer?.customer_name === customer.customer_name
                          ? "bg-blue-500/10 border-l-2 border-blue-500"
                          : "hover:bg-slate-100"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-800 text-sm">{customer.customer_name}</p>
                          <p className="text-xs text-slate-500">{customer.ders_adi}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          customer.latest_status?.includes("KAYIT") ? 'bg-emerald-500/20 text-emerald-400' :
                          customer.latest_status?.includes("CEVAP YOK") ? 'bg-red-500/20 text-red-400' :
                          customer.latest_status?.includes("DÜŞÜNECEK") ? 'bg-blue-500/20 text-blue-400' :
                          'bg-slate-300 text-slate-500'
                        }`}>
                          {customer.latest_status?.slice(0, 15)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Email Composer */}
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Mail size={18} className="text-blue-400" />
                E-posta Oluştur
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs text-slate-500">E-posta Adresi</label>
                  <Input
                    placeholder="ornek@email.com"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    className="bg-white border-slate-300"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs text-slate-500">Alıcı Adı</label>
                  <Input
                    placeholder="Müşteri adı"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    className="bg-white border-slate-300"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs text-slate-500">Şablon Seç</label>
                <Select onValueChange={(val) => {
                  const template = emailTemplates.find(t => t.id === val);
                  if (template) applyTemplate(template);
                }}>
                  <SelectTrigger className="bg-white border-slate-300">
                    <SelectValue placeholder="Şablon seçin..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-slate-800">
                    {emailTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-xs text-slate-500">Konu</label>
                <Input
                  placeholder="E-posta konusu"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="bg-white border-slate-300"
                />
              </div>

              <div className="space-y-2">
                <label className="text-xs text-slate-500">İçerik (HTML)</label>
                <Textarea
                  placeholder="E-posta içeriği..."
                  value={htmlContent}
                  onChange={(e) => setHtmlContent(e.target.value)}
                  className="bg-white border-slate-300 min-h-[150px] font-mono text-xs"
                />
              </div>

              <Button 
                onClick={sendEmail}
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={!recipientEmail || !subject || !htmlContent || sending}
              >
                {sending ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
                {sending ? "Gönderiliyor..." : "E-posta Gönder"}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Templates Tab */}
      {activeSubTab === "templates" && (
        <div className="grid gap-4 md:grid-cols-2">
          {emailTemplates.map(template => (
            <Card key={template.id} className="bg-white border-slate-200">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{template.name}</CardTitle>
                  <span className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded">
                    {template.category}
                  </span>
                </div>
                <p className="text-sm text-slate-500">{template.subject}</p>
              </CardHeader>
              <CardContent>
                <div className="bg-slate-100 p-3 rounded text-xs text-slate-700 max-h-32 overflow-auto" 
                     dangerouslySetInnerHTML={{ __html: template.html_content.slice(0, 300) + "..." }} />
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-3 w-full"
                  onClick={() => {
                    applyTemplate(template);
                    setActiveSubTab("send");
                  }}
                >
                  Bu Şablonu Kullan
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* History Tab */}
      {activeSubTab === "history" && (
        <Card className="bg-white border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <History size={18} className="text-slate-500" />
              E-posta Geçmişi
            </CardTitle>
            <Button size="sm" variant="ghost" onClick={onRefresh}>
              <RefreshCw size={14} />
            </Button>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <div className="space-y-3">
                {emailHistory.map((email, i) => (
                  <div key={email.id || i} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">{email.recipient_name || email.recipient_email}</span>
                        {email.status === "opened" ? (
                          <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded flex items-center gap-1">
                            <MailOpen size={10} /> Açıldı
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 bg-slate-300 text-slate-500 rounded flex items-center gap-1">
                            <Mail size={10} /> Gönderildi
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-slate-500">
                        {new Date(email.sent_at).toLocaleString('tr-TR')}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500">{email.recipient_email}</p>
                    <p className="text-sm text-slate-700 mt-1 font-medium">{email.subject}</p>
                    {email.opened_at && (
                      <p className="text-xs text-green-400 mt-1">
                        Açılma: {new Date(email.opened_at).toLocaleString('tr-TR')}
                      </p>
                    )}
                  </div>
                ))}
                {emailHistory.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <Mail size={32} className="mx-auto mb-2 opacity-50" />
                    <p>Henüz e-posta geçmişi yok</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Bulk Send Tab */}
      {activeSubTab === "bulk" && (
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Megaphone size={18} className="text-orange-400" />
              Toplu E-posta Gönderimi
            </CardTitle>
            <CardDescription>
              Belirli durumdaki müşterilere toplu e-posta gönderin
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs text-slate-500">Durum Filtresi</label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="bg-white border-slate-300">
                    <SelectValue placeholder="Durum seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-slate-800">
                    {statusOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-xs text-slate-500">Şablon</label>
                <Select onValueChange={(val) => {
                  const template = emailTemplates.find(t => t.id === val);
                  if (template) setSelectedTemplate(template);
                }}>
                  <SelectTrigger className="bg-white border-slate-300">
                    <SelectValue placeholder="Şablon seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-slate-800">
                    {emailTemplates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-slate-100 p-4 rounded-lg">
              <p className="text-sm text-slate-500">
                <strong className="text-slate-800">{filteredCustomers.length}</strong> müşteri seçili
              </p>
              {selectedTemplate && (
                <p className="text-sm text-slate-500 mt-1">
                  Şablon: <strong className="text-blue-400">{selectedTemplate.name}</strong>
                </p>
              )}
            </div>

            <Button 
              className="w-full bg-orange-600 hover:bg-orange-700"
              disabled={!selectedTemplate || filteredCustomers.length === 0}
              onClick={async () => {
                if (!selectedTemplate) return;
                setSending(true);
                try {
                  const res = await authAxios.post(`${API}/email/send-bulk`, {
                    template_id: selectedTemplate.id,
                    filter_status: filterStatus === "all" ? "" : filterStatus,
                    customer_ids: filteredCustomers.map(c => c.customer_name)
                  });
                  toast.success(res.data.message);
                  onRefresh();
                } catch (error) {
                  toast.error("Toplu gönderim başarısız");
                } finally {
                  setSending(false);
                }
              }}
            >
              {sending ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
              {sending ? "Gönderiliyor..." : `${filteredCustomers.length} Kişiye Gönder`}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Training Tab - Orientation and Training
const TrainingTab = ({ modules, progress, onComplete, onRefresh }) => {
  const [selectedModule, setSelectedModule] = useState(null);

  const completedCount = progress?.filter(p => p.completed)?.length || 0;
  const totalModules = modules?.length || 0;
  const progressPercent = totalModules > 0 ? Math.round((completedCount / totalModules) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Progress Overview */}
      <Card className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border-orange-500/30">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-slate-800">Oryantasyon İlerlemesi</h3>
              <p className="text-slate-500 mt-1">{completedCount} / {totalModules} modül tamamlandı</p>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold text-orange-400">%{progressPercent}</div>
            </div>
          </div>
          <div className="mt-4 h-3 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-orange-500 to-amber-500 transition-all"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Module List */}
      {!selectedModule ? (
        <div className="grid gap-4">
          {modules.map((module, i) => {
            const moduleProgress = progress?.find(p => p.module_id === module.id);
            const isCompleted = moduleProgress?.completed || module.completed;
            
            return (
              <Card 
                key={module.id} 
                className={`bg-white border-slate-200 cursor-pointer hover:border-orange-500/50 transition-all ${
                  isCompleted ? 'border-l-4 border-l-green-500' : ''
                }`}
                onClick={() => setSelectedModule(module)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl ${isCompleted ? 'bg-green-500/20' : 'bg-orange-500/20'}`}>
                        {isCompleted ? (
                          <CheckCircle size={24} className="text-green-400" />
                        ) : (
                          <PlayCircle size={24} className="text-orange-400" />
                        )}
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-800">{module.title}</h4>
                        <p className="text-sm text-slate-500">{module.description}</p>
                        <span className="text-xs text-slate-400 mt-1 block">
                          {module.category} • {module.is_required ? 'Zorunlu' : 'Opsiyonel'}
                        </span>
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-slate-500" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {modules.length === 0 && (
            <Card className="bg-white border-slate-200">
              <CardContent className="p-8 text-center">
                <GraduationCap size={48} className="mx-auto text-slate-400 mb-4" />
                <p className="text-slate-500">Henüz eğitim modülü eklenmemiş</p>
                <p className="text-xs text-slate-400 mt-1">Admin tarafından modüller eklenecektir</p>
              </CardContent>
            </Card>
          )}
        </div>
      ) : (
        /* Module Detail View */
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={() => setSelectedModule(null)}>
                ← Geri
              </Button>
              {!selectedModule.completed && (
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={async () => {
                    await onComplete(selectedModule.id);
                    setSelectedModule(null);
                  }}
                >
                  <CheckCircle size={16} className="mr-2" />
                  Tamamlandı Olarak İşaretle
                </Button>
              )}
            </div>
            <CardTitle className="text-xl">{selectedModule.title}</CardTitle>
            <CardDescription>{selectedModule.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div 
              className="prose prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: selectedModule.content }}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Main Agent Dashboard Page
const AgentDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [agentName, setAgentName] = useState("");
  const [agentId, setAgentId] = useState("");
  const [dashboardData, setDashboardData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  // WhatsApp states
  const [customersWithPhone, setCustomersWithPhone] = useState([]);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [messageHistory, setMessageHistory] = useState([]);
  // Email states
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [customersForEmail, setCustomersForEmail] = useState([]);
  // Training states
  const [trainingModules, setTrainingModules] = useState([]);
  const [trainingProgress, setTrainingProgress] = useState([]);
  // New data notification
  const [newDataInfo, setNewDataInfo] = useState(null);
  const prevNewCountRef = useRef(0);

  // Notification tick sound
  const playTickSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [0, 0.15].forEach(delay => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 880;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.3, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.1);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.1);
      });
    } catch (e) { console.log('Sound error:', e); }
  }, []);

  // Poll for new data every 30s
  useEffect(() => {
    if (!agentId) return;
    const checkNewData = async () => {
      try {
        const res = await authAxios.get(`${API}/agent/my-new-data-count`);
        const data = res.data;
        const currentCount = (data.new_today || 0) + (data.uncalled_count || 0);
        if (currentCount > prevNewCountRef.current && prevNewCountRef.current > 0) {
          playTickSound();
          toast.info(`Yeni data geldi! ${data.uncalled_count} aranmayan data mevcut`, { duration: 5000 });
        }
        prevNewCountRef.current = currentCount;
        setNewDataInfo(data);
      } catch (e) { /* silent */ }
    };
    checkNewData();
    const interval = setInterval(checkNewData, 30000);
    return () => clearInterval(interval);
  }, [agentId, playTickSound]);

  // Check auth and load initial data
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("authToken");
      const role = localStorage.getItem("userRole");
      
      if (!token || role !== "agent") {
        navigate("/login");
        return;
      }

      try {
        const res = await authAxios.get(`${API}/auth/me`);
        if (res.data.role !== "agent") {
          navigate("/login");
          return;
        }
        setAgentName(res.data.agent_name || "Danışman");
        setAgentId(res.data.agent_id);
      } catch (error) {
        console.error("Auth check failed:", error);
        navigate("/login");
      }
    };

    checkAuth();
  }, [navigate]);

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    try {
      const [dashRes, chartRes] = await Promise.all([
        authAxios.get(`${API}/agent/dashboard`),
        authAxios.get(`${API}/agent/stats/chart?period=week`)
      ]);
      setDashboardData(dashRes.data);
      setChartData(chartRes.data);
    } catch (error) {
      console.error("Dashboard load error:", error);
    }
  }, []);

  // Load customers
  const loadCustomers = useCallback(async () => {
    try {
      const res = await authAxios.get(`${API}/agent/customers`);
      setCustomers(res.data);
    } catch (error) {
      console.error("Customers load error:", error);
    }
  }, []);

  // Load templates
  const loadTemplates = useCallback(async () => {
    try {
      const res = await authAxios.get(`${API}/templates`);
      setTemplates(res.data);
    } catch (error) {
      console.error("Templates load error:", error);
    }
  }, []);

  // Load WhatsApp data
  const loadWhatsAppData = useCallback(async () => {
    try {
      const [customersRes, templatesRes, historyRes] = await Promise.all([
        authAxios.get(`${API}/agent/customers-with-phone`),
        authAxios.get(`${API}/whatsapp/templates`),
        authAxios.get(`${API}/whatsapp/history`)
      ]);
      setCustomersWithPhone(customersRes.data || []);
      setWhatsappTemplates(templatesRes.data || []);
      setMessageHistory(historyRes.data || []);
    } catch (error) {
      console.error("WhatsApp data load error:", error);
    }
  }, []);

  // Load Email data
  const loadEmailData = useCallback(async () => {
    try {
      const [templatesRes, historyRes, statsRes, customersRes] = await Promise.all([
        authAxios.get(`${API}/email/templates`),
        authAxios.get(`${API}/email/history`),
        authAxios.get(`${API}/email/stats`),
        authAxios.get(`${API}/agent/customers-with-email`)
      ]);
      setEmailTemplates(templatesRes.data || []);
      setEmailHistory(historyRes.data || []);
      setEmailStats(statsRes.data || {});
      setCustomersForEmail(customersRes.data || []);
    } catch (error) {
      console.error("Email data load error:", error);
    }
  }, []);

  // Load Training data
  const loadTrainingData = useCallback(async () => {
    try {
      const res = await authAxios.get(`${API}/training/progress`);
      setTrainingModules(res.data || []);
      setTrainingProgress(res.data || []);
    } catch (error) {
      console.error("Training data load error:", error);
    }
  }, []);

  // Complete training module
  const completeTrainingModule = async (moduleId) => {
    try {
      await authAxios.post(`${API}/training/progress/${moduleId}/complete`);
      toast.success("Modül tamamlandı!");
      loadTrainingData();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  // Load data based on active tab
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      if (activeTab === "dashboard") {
        await loadDashboard();
      } else if (activeTab === "customers") {
        await loadCustomers();
      } else if (activeTab === "templates") {
        await loadTemplates();
      } else if (activeTab === "whatsapp") {
        await loadWhatsAppData();
      } else if (activeTab === "email") {
        await loadEmailData();
      } else if (activeTab === "training") {
        await loadTrainingData();
      }
      
      setLoading(false);
    };

    if (agentId) {
      loadData();
    }
  }, [activeTab, agentId, loadDashboard, loadCustomers, loadTemplates, loadWhatsAppData, loadEmailData, loadTrainingData]);

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userRole");
    localStorage.removeItem("agentId");
    localStorage.removeItem("agentName");
    navigate("/login");
  };

  // Render active tab content
  const renderContent = () => {
    switch (activeTab) {
      case "dashboard":
        return <DashboardTab dashboardData={dashboardData} chartData={chartData} newDataInfo={newDataInfo} />;
      case "whatsapp":
        return (
          <WhatsAppTab 
            customersWithPhone={customersWithPhone}
            whatsappTemplates={whatsappTemplates}
            messageHistory={messageHistory}
            onRefresh={loadWhatsAppData}
          />
        );
      case "email":
        return (
          <EmailTab
            emailTemplates={emailTemplates}
            emailHistory={emailHistory}
            emailStats={emailStats}
            customers={customersForEmail}
            onRefresh={loadEmailData}
          />
        );
      case "training":
        return (
          <TrainingTab
            modules={trainingModules}
            progress={trainingProgress}
            onComplete={completeTrainingModule}
            onRefresh={loadTrainingData}
          />
        );
      case "customers":
        return <CustomersTab customers={customers} />;
      case "my-data":
        return <CRMDataTracker token={localStorage.getItem("authToken")} isAdmin={false} />;
      case "templates":
        return <TemplatesTab templates={templates} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 admin-light-theme flex">
      <Toaster position="top-right" theme="dark" />
      
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={setActiveTab}
        agentName={agentName}
        onLogout={handleLogout}
        uncalledCount={newDataInfo?.uncalled_count || 0}
      />

      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw size={32} className="animate-spin text-purple-400" />
            </div>
          ) : (
            renderContent()
          )}
        </div>
      </main>
    </div>
  );
};

export default AgentDashboard;
